import React, { useEffect, useState } from "react";
import { getAppParams } from "../utils/appParams";
import { FoxitWebPDFApp } from "../components/FoxitWebPDFApp";
import { PDFViewerRenderer } from "../components/PDFViewerRenderer";
import { PDFUIInstanceContext } from "../components/FoxitWebPDFContexts";
import Loader from "../components/loader";
import DebugErrorScreen from "../components/DebugErrorScreen";
import {
  fetchBlobBySharePointIds,
  fetchSharePointItemBlobFromUrl,
  fetchSharePointItemBlobByDriveRef,
} from "../utils/graph";

export default function PdfViewer() {
  const [fileUrl, setFileUrl] = useState(null);
  const [fileBlob, setFileBlob] = useState(null);
  const [foxitReady, setFoxitReady] = useState(false);
  const [opened, setOpened] = useState(false);
  const [noSource, setNoSource] = useState(false);
  const [error, setError] = useState(null);

  // Inject Foxit CSS and JS
  useEffect(() => {
    if (window.UIExtension && window.UIExtension.PDFUI) return;
    const env = (typeof window !== 'undefined' && window.env) ? window.env : {};
    const configuredBase = env.REACT_APP_FOXIT_LIB_BASE;
    const LOCAL_BASE = configuredBase || '/foxit-lib/';
    function injectCss(href) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
      return link;
    }
    function injectScript(src, onload, onerror) {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = onload;
      if (onerror) s.onerror = onerror;
      document.head.appendChild(s);
      return s;
    }
    injectCss(LOCAL_BASE + 'UIExtension.css');
    window.__foxitLibBase = LOCAL_BASE;
    injectScript(LOCAL_BASE + 'UIExtension.full.js', () => {
      // Loaded
    });
  }, []);

  // Fetch PDF using Graph API (via ServiceNow token)
  useEffect(() => {
    const urlParams = getAppParams();
    const siteId = urlParams.get('siteId');
    const listId = urlParams.get('listId');
    const listItemId = urlParams.get('listItemId');
    const driveId = urlParams.get('driveId');
    const itemId = urlParams.get('itemId');
    const url = urlParams.get('url');

    (async () => {
      try {
        let blob = null;
        if (url) {
          // Storage-agnostic dispatch: a pre-authorized direct URL
          // (e.g. an Azure Blob SAS from Lore) needs no Graph lookup —
          // fetch it as-is. Only SharePoint URLs go through the Graph
          // /shares resolution. Fixes "Graph shares lookup failed:
          // 403 Forbidden" for non-SharePoint documents (2026-07-17).
          let parsed = null;
          try { parsed = new URL(url); } catch { parsed = null; }
          const isSharePoint = parsed && /(^|\.)sharepoint(-df)?\.com$/i.test(parsed.hostname);
          if (parsed && !isSharePoint) {
            console.log('[PdfViewer] Direct fetch (non-SharePoint):', parsed.origin + parsed.pathname);
            const resp = await fetch(url);
            if (!resp.ok) {
              throw new Error(`Direct fetch failed: ${resp.status} ${resp.statusText}`);
            }
            blob = await resp.blob();
          } else {
            const decodedUrl = decodeURIComponent(url);
            console.log('[PdfViewer] URL param:', decodedUrl);
            blob = await fetchSharePointItemBlobFromUrl(decodedUrl);
          }
        } else if (siteId && listId && listItemId) {
          console.log('[PdfViewer] Using SharePoint IDs path');
          blob = await fetchBlobBySharePointIds({ siteId, listId, listItemId });
        } else if (driveId && itemId) {
          console.log('[PdfViewer] Using drive+item path');
          blob = await fetchSharePointItemBlobByDriveRef(driveId, itemId);
        } else {
          console.error('[PdfViewer] No PDF source provided');
          setNoSource(true);
          return;
        }

        if (blob) {
          console.log('[PdfViewer] PDF loaded, size:', blob.size);
          setFileBlob(blob);
        } else {
          setError({
            code: "FETCH_PDF_FAILED",
            message: "Failed to load PDF - no data received",
            details: {
              url: window?.location?.href,
            },
          });
        }
      } catch (e) {
        console.error('[PdfViewer] Failed to fetch PDF:', e);
        setError({
          code: "FETCH_PDF_FAILED",
          message: `Failed to load PDF: ${e?.message || String(e)}`,
          error: e,
          details: {
            url: window?.location?.href,
          },
        });
      }
    })();
  }, []);

  // Check for Foxit ready
  useEffect(() => {
    let timeoutId;
    let attempts = 0;
    const maxAttempts = 300;
    const checkFoxitReady = () => {
      attempts++;
      if (window.UIExtension && window.UIExtension.PDFUI) {
        console.log('[Foxit] UIExtension ready after attempts', attempts);
        setFoxitReady(true);
      } else if (attempts < maxAttempts) {
        timeoutId = setTimeout(checkFoxitReady, 100);
      } else {
        console.error("UIExtension failed to load after timeout", window.__foxitLibBase);
        setError({
          code: "FOXIT_LOAD_FAILED",
          message: "Failed to load PDF viewer - please refresh the page",
          details: {
            foxitLibBase: window?.__foxitLibBase,
            url: window?.location?.href,
          },
        });
      }
    };
    checkFoxitReady();
    return () => { if (timeoutId) clearTimeout(timeoutId); };
  }, []);

  if (noSource) {
    return (
      <div style={{ position: "relative", height: "100vh" }}>
        <DebugErrorScreen
          title="PDF viewer error"
          error="No PDF source provided. Please open from Smart Folders."
          details={{
            code: "NO_PDF_SOURCE",
            url: window?.location?.href,
          }}
          allowDismiss={false}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ position: "relative", height: "100vh" }}>
        <DebugErrorScreen
          title="PDF viewer error"
          error={error?.error || error?.message || error}
          details={typeof error === "string" ? { message: error, url: window?.location?.href } : error}
          allowDismiss={false}
        />
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', overflow: 'hidden', position: 'relative' }}>
      {foxitReady && (fileUrl || fileBlob) && window.UIExtension ? (
        <FoxitWebPDFApp
          fileUrl={fileUrl}
          fileBlob={fileBlob}
          onOpenStart={() => {}}
          onOpened={() => setOpened(true)}
          onOpenError={() => {}}
        >
          <PDFViewerRenderer />
          <PDFUIInstanceContext.Consumer>
            {(pdfui) => {
              return <></>;
            }}
          </PDFUIInstanceContext.Consumer>
        </FoxitWebPDFApp>
      ) : (
        <Loader message="Loading PDF viewer..." />
      )}

      {(fileUrl || fileBlob) && (!opened) && (
        <Loader message="Loading PDF..." />
      )}
    </div>
  );
}
